/**
 * Delivery hours for a message step: when it may reach a person.
 *
 * Phrased throughout as when messages DO arrive rather than when they are
 * suppressed. That is how the server stores it, and it is also the version a
 * reader can check at a glance: "Mon to Fri, 7:00 AM to 5:00 PM" says what
 * happens, where a list of quiet ranges makes you work out the gaps.
 */
import { Input } from '@servicecompressionllc/sc-design-system';
import type { BuilderDeliverWindow } from '../../lib/types.js';
import { WeekdayPicker } from './WeekdayPicker.js';

/** What switching the control on starts you with: a normal working week. */
export const DEFAULT_WINDOW: BuilderDeliverWindow = {
  days: [1, 2, 3, 4, 5],
  start: '07:00',
  end: '17:00',
};

interface Props {
  value: BuilderDeliverWindow | null | undefined;
  onChange: (next: BuilderDeliverWindow | null) => void;
}

export const DeliverWindowEditor = ({ value, onChange }: Props): React.ReactElement => {
  const on = Boolean(value);
  const w = value ?? DEFAULT_WINDOW;

  return (
    <div className="grid gap-3 border-t border-outline pt-3">
      <label className="flex items-center gap-2 text-xs font-semibold text-ink-muted">
        <input
          type="checkbox"
          className="accent-primary"
          checked={on}
          onChange={(e) => onChange(e.target.checked ? DEFAULT_WINDOW : null)}
        />
        Only send during set hours
      </label>

      {!on && (
        <p className="text-xs text-ink-subtle">
          Messages go out as soon as they happen, day or night.
        </p>
      )}

      {on && (
        <div className="grid gap-3">
          <div className="grid gap-1">
            <span className="text-xs text-ink-muted">Days</span>
            <WeekdayPicker days={w.days} onChange={(days) => onChange({ ...w, days })} />
          </div>

          <div className="flex items-end gap-3">
            <label className="grid gap-1 text-xs text-ink-muted">
              From
              <Input
                type="time"
                aria-label="Delivery starts"
                value={w.start}
                onChange={(e) => onChange({ ...w, start: e.target.value })}
                className="w-32"
              />
            </label>
            <label className="grid gap-1 text-xs text-ink-muted">
              To
              <Input
                type="time"
                aria-label="Delivery ends"
                value={w.end}
                onChange={(e) => onChange({ ...w, end: e.target.value })}
                className="w-32"
              />
            </label>
          </div>

          {w.start >= w.end && (
            <p className="text-xs text-ink-muted">
              The start time needs to come before the end time. Hours that run past midnight are not
              supported yet.
            </p>
          )}

          <p className="text-xs text-ink-subtle">
            Messages raised outside these hours are not sent. To cover the quiet period, use a
            workflow that runs on a schedule and reports what happened.
          </p>
        </div>
      )}
    </div>
  );
};
